/**
 * Shared UI atoms used across admin-console screens.
 *
 * These are intentionally lightweight — no compound components, no
 * polymorphic-as-prop, no headless library. Admin screens iterate fast
 * and visual consistency matters more than ergonomic flourishes.
 *
 * Conventions:
 *   - Cards use bg-nurev-surface + border-nurev-border for the resting
 *     card surface. Lifted content (modals, sub-panels) uses
 *     bg-nurev-surface-lift for visual contrast.
 *   - Amber is the admin-console accent (vs. purple on the inference
 *     console / emerald on the fleet console). Status colors stay
 *     consistent across surfaces (emerald = ok, amber = pending,
 *     red = bad, slate = neutral).
 */
import { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';

// ─── Card ─────────────────────────────────────────────────────────────────

export function Card({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-nurev-border bg-nurev-surface p-5 ${className}`}
    >
      {children}
    </div>
  );
}

// ─── Section label ────────────────────────────────────────────────────────

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-4 text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
      {children}
    </p>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-white/[0.04] ${className}`} />;
}

// ─── Status badges ────────────────────────────────────────────────────────

export const STATUS_BADGE: Record<string, string> = {
  // Positive
  installed: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  active: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  resolved: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  completed: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  // Warning / in-flight
  pending: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  open: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  in_progress: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  // Assigned / informational
  assigned: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  // Bad
  uninstalled: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  rejected: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  failed: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  // Neutral
  inactive: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
  dismissed: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
};

export const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  high: 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30',
  normal: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  low: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
};

export function Badge({
  value,
  variant = 'status'
}: {
  value?: string | null;
  variant?: 'status' | 'priority';
}) {
  const map = variant === 'priority' ? PRIORITY_BADGE : STATUS_BADGE;
  const cls = map[value || ''] || 'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {value || '—'}
    </span>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────

export function StatCard({
  label,
  value,
  sub,
  color,
  loading
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  loading?: boolean;
}) {
  return (
    <Card className="p-4 lg:p-5">
      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-nurev-muted lg:mb-2 lg:text-xs">
        {label}
      </p>
      {loading ? (
        <Skeleton className="h-7 w-28" />
      ) : (
        <>
          <p
            className={`text-lg font-bold leading-tight lg:text-2xl ${
              color || 'text-nurev-text'
            }`}
          >
            {value}
          </p>
          {sub && (
            <p className="mt-0.5 text-[10px] text-nurev-muted lg:text-xs">{sub}</p>
          )}
        </>
      )}
    </Card>
  );
}

// ─── Detail cell — used inside Card for KPI grids ─────────────────────────

export function DetailCell({
  label,
  children,
  mono,
  capitalize,
  upper,
  color
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
  capitalize?: boolean;
  upper?: boolean;
  color?: string;
}) {
  const cls = [
    'text-sm',
    mono && 'font-mono',
    capitalize && 'capitalize',
    upper && 'uppercase',
    color || 'text-nurev-text'
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div>
      <p className="text-[10px] uppercase text-nurev-muted">{label}</p>
      <p className={cls}>{children}</p>
    </div>
  );
}

// ─── Back button — used on detail panels ──────────────────────────────────

export function BackButton({
  onClick,
  label = 'Back'
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="group inline-flex items-center gap-1.5 text-xs text-amber-300 transition-colors hover:text-amber-200"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
      {label}
    </button>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────

export function Pagination({
  page,
  totalPages,
  onChange
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="mt-4 flex justify-center gap-2">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="rounded bg-white/[0.06] px-3 py-1 text-xs text-nurev-muted transition-all hover:-translate-y-0.5 hover:bg-white/10 active:translate-y-0 disabled:opacity-30 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0"
      >
        Prev
      </button>
      <span className="px-3 py-1 text-xs text-nurev-muted">
        {page} / {totalPages}
      </span>
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="rounded bg-white/[0.06] px-3 py-1 text-xs text-nurev-muted transition-all hover:-translate-y-0.5 hover:bg-white/10 active:translate-y-0 disabled:opacity-30 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0"
      >
        Next
      </button>
    </div>
  );
}

// ─── Select (filter dropdown) ─────────────────────────────────────────────

export function Select<T extends string>({
  value,
  onChange,
  options,
  className = ''
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value as T)}
      className={`rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none ${className}`}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── Action button (primary amber) ────────────────────────────────────────

export function ActionButton({
  onClick,
  disabled,
  type = 'button',
  children,
  variant = 'primary',
  size = 'md'
}: {
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
  variant?: 'primary' | 'positive' | 'destructive' | 'neutral' | 'info';
  size?: 'sm' | 'md';
}) {
  const variants: Record<string, string> = {
    primary:
      'bg-amber-500 text-nurev-bg hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/25',
    positive:
      'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-500/30 hover:shadow-md hover:shadow-emerald-500/15',
    destructive:
      'bg-red-500/20 text-red-300 ring-1 ring-red-500/30 hover:bg-red-500/30 hover:shadow-md hover:shadow-red-500/15',
    neutral:
      'bg-white/10 text-nurev-muted ring-1 ring-white/10 hover:bg-white/15 hover:shadow-md hover:shadow-black/20',
    info: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30 hover:bg-sky-500/30 hover:shadow-md hover:shadow-sky-500/15'
  };
  const sizes: Record<string, string> = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm'
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg font-medium transition-all duration-150 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-nurev-bg disabled:opacity-30 disabled:hover:translate-y-0 disabled:hover:shadow-none motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${variants[variant]} ${sizes[size]}`}
    >
      {children}
    </button>
  );
}
